// src/layouts/AppSidebarLayout.tsx
import type { ReactNode, MouseEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState } from "../../store";
import { clearUser } from "../../store/slices/authSlice";
import { auth } from "../../config/firebaseConfig";
import logoUrl from '../../assets/qid-logo.clean.svg';

import { Avatar } from "../../components/catalyst/avatar";
import {
  Dropdown, DropdownButton, DropdownDivider, DropdownItem, DropdownLabel, DropdownMenu,
} from "../../components/catalyst/dropdown";
import {
  Sidebar as CxSidebar,
  SidebarBody, SidebarFooter, SidebarHeader, SidebarItem, SidebarLabel, SidebarSection,
} from "../../components/catalyst/sidebar";
import { SidebarLayout } from "../../components/catalyst/sidebar-layout";

import {
  ArrowRightStartOnRectangleIcon, ChevronUpIcon, Cog8ToothIcon, UserIcon,
} from "@heroicons/react/16/solid";
import {
  DocumentTextIcon, UsersIcon, Squares2X2Icon, RectangleStackIcon,
  ExclamationTriangleIcon, CreditCardIcon, UserCircleIcon, BeakerIcon,
} from "@heroicons/react/20/solid";

type Props = { children?: ReactNode };

export default function AppSidebarLayout({ children }: Props) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { user } = useSelector((s: RootState) => s.auth);

  const go = (to: string) => (e?: MouseEvent) => { e?.preventDefault(); navigate(to); };

  const initials =
    (user?.displayName || user?.email || "?")
      .split(" ").map(s => s[0]?.toUpperCase()).slice(0, 2).join("") || "?";

  // const activeCls =
  //   "relative rounded-xl text-brand-600 " +
  //   "before:absolute before:left-1 before:top-1 before:bottom-1 before:w-1 before:rounded before:bg-brand-600";
  // const idleCls = "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900";

  const activeCls =
  "relative flex items-center gap-3 rounded-xl bg-indigo-500/10 text-indigo-700 " +
  "before:absolute before:left-1 before:top-1 before:bottom-1 before:w-1 before:rounded-full before:bg-indigo-600 " +
  "dark:bg-indigo-400/10 dark:text-indigo-300 before:dark:bg-indigo-300";

// Idle sidebar item (hover effect)
const idleCls =
  "text-zinc-600 hover:bg-indigo-500/10 hover:text-indigo-700 " +
  "dark:text-zinc-400 dark:hover:bg-indigo-400/10 dark:hover:text-indigo-300 transition-all duration-200";
  const iconCls = (on: boolean) => (on ? "h-5 w-5 text-brand-700 shrink-0" : "h-5 w-5 text-zinc-400 shrink-0");

  const items = [
    { to: "/admin", label: "Reports",rolesAllowed: ["admin","approver", "analyst"], icon: (a: boolean) => <DocumentTextIcon className={iconCls(a)} />, isActive: (p: string) => p === "/admin" || p.startsWith("/admin/reports") },
    { to: "/admin/members", label: "Members", rolesAllowed: ["admin","approver","analyst"], icon: (a: boolean) => <UsersIcon className={iconCls(a)} />, isActive: (p: string) => p.startsWith("/admin/members") },
    { to: "/admin/templates", label: "Templates",rolesAllowed: ["admin","approver", "analyst"], icon: (a: boolean) => <Squares2X2Icon className={iconCls(a)} />, isActive: (p: string) => p.startsWith("/admin/templates") },
    { to: "/admin/layers", label: "Layers",rolesAllowed: ["admin","approver", "analyst"], icon: (a: boolean) => <RectangleStackIcon className={iconCls(a)} />, isActive: (p: string) => p.startsWith("/admin/layers") },
    { to: "/admin/annexures", label: "Annexures",rolesAllowed: ["admin","approver", "analyst"], icon: (a: boolean) => <ExclamationTriangleIcon className={iconCls(a)} />, isActive: (p: string) => p.startsWith("/admin/annexures") },
    { to: "/admin/payments", label: "Payments",rolesAllowed: ["admin"], icon: (a: boolean) => <CreditCardIcon className={iconCls(a)} />, isActive: (p: string) => p.startsWith("/admin/payments") },
    { to: "/admin/administrator", label: "Administrators",rolesAllowed: ["admin"], icon: (a: boolean) => <UserCircleIcon className={iconCls(a)} />, isActive: (p: string) => p.startsWith("/admin/admins") },
    { to: "/admin/tryout", label: "Try it out",rolesAllowed: ["admin","approver"], icon: (a: boolean) => <BeakerIcon className={iconCls(a)} />, isActive: (p: string) => p.startsWith("/admin/tryout") },
  ];

  const handleLogout = async () => {
    try {
      localStorage.removeItem("idToken");
      localStorage.removeItem("refreshToken");
      await auth.signOut();
    } finally {
      dispatch(clearUser());
      navigate("/auth/login", { replace: true });
    }
  };

  return (
    <SidebarLayout
      navbar={
        <div className="h-50 bg-zinc-50 border-b border-zinc-200 flex items-center px-6">
          {/* Empty top space, can add title/logo if needed */}
        </div>
      }
      sidebar={
        <CxSidebar className="bg-brand-softer">
          <SidebarHeader>
            <div className="flex items-center gap-3 px-2 pt-12">
              <img src="/assets/QID-wordmark.svg" alt="QID Logo" className="h-8 w-auto" />
            </div>
          </SidebarHeader>

          <SidebarBody>
            <SidebarSection>
            {items
              .filter(item => item.rolesAllowed.includes(user?.role || ""))
              .map(({ to, label, icon, isActive }) => {
                const on = isActive(pathname);
                return (
                  <SidebarItem
                    key={to}
                    href={to}
                    current={on}
                    aria-current={on ? "page" : undefined}
                    onClick={go(to)}
                    className={on ? activeCls : idleCls}
                  >
                    {icon(on)}
                    <SidebarLabel>{label}</SidebarLabel>
                  </SidebarItem>
                );
              })}
            </SidebarSection>
          </SidebarBody>

          <SidebarFooter>
            <Dropdown>
              <DropdownButton as={SidebarItem} className="rounded-xl">
                <span className="flex min-w-0 items-center gap-3">
                  <Avatar className="h-8 w-8" initials={initials} square alt="" />
                  <span className="min-w-0">
                    <span className="block truncate text-sm/5 font-medium text-zinc-950 dark:text-white">
                      {user?.displayName || "User"}
                    </span>
                    <span className="block truncate text-xs/5 font-normal text-zinc-500 dark:text-zinc-400">
                      {user?.email}
                    </span>
                  </span>
                </span>
                <ChevronUpIcon className="h-4 w-4 shrink-0" />
              </DropdownButton>
              <DropdownMenu className="w-64" anchor="top start">
                <DropdownItem onClick={handleLogout}>
                  <ArrowRightStartOnRectangleIcon className="h-4 w-4 shrink-0" />
                  <DropdownLabel>Sign out</DropdownLabel>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </SidebarFooter>
        </CxSidebar>
      }
    >
      {children}
    </SidebarLayout>
  );
}
